import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Theme } from "../../../common/themes/theme";
import Eyebrow from "../../../common/components/eyebrow/Eyebrow";
import H2 from "../../../common/components/text/H2";
import { HeadingBlock } from "../../../common/components/layout/HeadingBlock";
import Description from "../../../common/components/text/Description";
import { Icon } from "@iconify/react";
import PrimarySearch from "../../../common/components/input/PrimarySearch";
import { FaqStore } from "../../../assets/store/faq.store";

const { color, font, border } = Theme;

function FaqAnswer({ isOpen, children }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.scrollHeight);
    }
  }, [children]);

  return (
    <FaqAOuter
      style={{
        maxHeight: isOpen ? `${height}px` : "0px",
        opacity: isOpen ? 1 : 0,
      }}
    >
      <div ref={ref}>{children}</div>
    </FaqAOuter>
  );
}

const Wrapper = styled.section`
  background: ${color.background.light};
  font-family: ${font.fontFamily.sans};
`;

const Header = styled.div`
  padding: 72px 50px 56px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;
  border-bottom: 1px solid ${color.border.light};

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 48px 20px 40px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    align-items: flex-start;
    width: 100%;
  }
`;

const CatTabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    width: 100%;
  }
`;

const CatTab = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  font-family: ${font.fontFamily.sans[0]}, sans-serif;
  font-size: ${font.fontSize.sm};
  font-weight: ${font.fontWeight.medium};
  letter-spacing: ${font.letterSpacing.wide};
  color: ${({ $active }) =>
    $active ? color.text.inverse : color.text.secondary};
  background: ${({ $active }) =>
    $active ? color.text.primary : color.surface.light};
  border: 1px solid
    ${({ $active }) => ($active ? color.text.primary : color.border.light)};
  border-radius: ${border.borderRadius.sm};
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 14px;
  }

  &:hover:not([disabled]) {
    border-color: ${({ $active }) =>
      $active ? color.text.primary : "#d0cbc8"};
    background: ${({ $active }) =>
      $active ? color.text.primary : color.background.subtle};
    color: ${({ $active }) =>
      $active ? color.text.inverse : color.text.neutral};
  }

  svg {
    height: 16px;
    width: 16px;
  }
`;

const Body = styled.div`
  padding: 0 50px 72px;

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    padding: 0 20px 56px;
  }
`;

const CatGroup = styled.div`
  padding-top: 48px;
`;

const CatLabel = styled.div`
  font-family: ${font.fontFamily.heading};
  font-size: ${font.fontSize.md};
  font-weight: ${font.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: ${font.letterSpacing.widest};
  color: ${color.text.secondary};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${color.text.primary};
    flex-shrink: 0;
  }
`;

const FaqGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: ${color.border.light};
  border: 1px solid ${color.border.light};
  border-radius: ${border.borderRadius.md};
  overflow: hidden;
`;

const FaqItem = styled.div`
  background: ${({ $open }) =>
    $open ? color.background.subtle : color.surface.light};
  transition: background 0.25s ease;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${color.text.primary};
    transform: scaleY(${({ $open }) => ($open ? 1 : 0)});
    transform-origin: top;
    transition: transform 0.3s ease;
  }
`;

const FaqQ = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px;
  cursor: pointer;
  user-select: none;
`;

const FaqQText = styled.span`
  font-size: ${font.fontSize.base};
  font-weight: ${font.fontWeight.medium};
  color: ${({ $open }) => ($open ? color.text.primary : color.text.neutral)};
  line-height: ${font.lineHeight.snug};
  transition: color 0.2s ease;
  padding-top: 1px;
`;

const FaqIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  background: ${({ $open }) =>
    $open ? color.text.primary : color.background.light};
  border: 1px solid
    ${({ $open }) => ($open ? color.text.primary : color.border.light)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $open }) => ($open ? color.text.inverse : color.text.neutral)};
  font-size: 13px;
  transform: ${({ $open }) => ($open ? "rotate(45deg)" : "rotate(0deg)")};
  transition:
    transform 0.3s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
  margin-top: 1px;

  svg {
    height: 16px;
    width: 16px;
  }
`;

const FaqAOuter = styled.div`
  overflow: hidden;
  transition:
    max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;
`;

const FaqAInner = styled.div`
  padding: 0 24px 22px;
  border-top: 1px solid ${color.border.light};
  padding-top: 16px;
`;

const FaqAText = styled.p`
  font-size: ${font.fontSize.base};
  color: ${color.text.secondary};
  line-height: ${font.lineHeight.relaxed};
  margin: 0;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

const Tag = styled.span`
  display: inline-block;
  font-size: ${font.fontSize.xs};
  font-weight: ${font.fontWeight.medium};
  letter-spacing: ${font.letterSpacing.wider};
  text-transform: uppercase;
  color: ${color.text.primary};
  background: ${color.surface.red};
  border: 1px solid ${color.background.red};
  padding: 2px 8px;
  border-radius: ${border.borderRadius.sm};
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: ${color.text.secondary};
  font-size: ${font.fontSize.base};
  background: ${color.surface.light};
  border: 1px solid ${color.border.light};
  border-radius: ${border.borderRadius.md};
  margin-top: 32px;
`;

function FAQSection() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [openIndex, setOpenIndex] = useState(null);
  const [search, setSearch] = useState("");

  const handleToggle = (key) => {
    setOpenIndex((prev) => (prev === key ? null : key));
  };

  const filteredFaqs = FaqStore.faqs.filter((f) => {
    const matchesCat = activeCategory === "all" || f.cat === activeCategory;
    const matchesSearch =
      !search ||
      f.q.toLowerCase().includes(search.toLowerCase()) ||
      f.a.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const visibleCats = ["quoting", "technical", "shipping"].filter((cat) =>
    filteredFaqs.some((f) => f.cat === cat),
  );

  return (
    <Wrapper>
      <Header>
        <HeadingBlock>
          <Eyebrow text="Support" />
          <H2>
            Frequently Asked <span>Questions</span>
          </H2>
          <Description>
            Everything you need to know about our process, capabilities,
            pricing, and lead times.
          </Description>
        </HeadingBlock>

        <HeaderRight>
          <CatTabs>
            {FaqStore.categories.map((cat) => (
              <CatTab
                key={cat.id}
                $active={activeCategory === cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setOpenIndex(null);
                }}
              >
                <Icon icon={cat.icon} />
                {cat.label}
              </CatTab>
            ))}
          </CatTabs>

          <PrimarySearch
            type="text"
            placeholder="Search questions..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpenIndex(null);
            }}
          />
        </HeaderRight>
      </Header>

      <Body>
        {filteredFaqs.length === 0 ? (
          <EmptyState>
            No questions found. Try a different search term.
          </EmptyState>
        ) : (
          visibleCats.map((cat) => {
            const items = filteredFaqs.filter((f) => f.cat === cat);
            if (!items.length) return null;
            return (
              <CatGroup key={cat}>
                <CatLabel>{FaqStore.catLabels[cat]}</CatLabel>
                <FaqGrid>
                  {items.map((faq, i) => {
                    const key = `${cat}-${i}`;
                    const isOpen = openIndex === key;
                    return (
                      <FaqItem key={key} $open={isOpen}>
                        <FaqQ onClick={() => handleToggle(key)}>
                          <FaqQText $open={isOpen}>{faq.q}</FaqQText>
                          <FaqIcon $open={isOpen}>
                            <Icon icon={"ic:round-plus"} />
                          </FaqIcon>
                        </FaqQ>
                        <FaqAnswer isOpen={isOpen}>
                          <FaqAInner>
                            <FaqAText>{faq.a}</FaqAText>
                            {faq.tags.length > 0 && (
                              <TagRow>
                                {faq.tags.map((t) => (
                                  <Tag key={t}>{t}</Tag>
                                ))}
                              </TagRow>
                            )}
                          </FaqAInner>
                        </FaqAnswer>
                      </FaqItem>
                    );
                  })}
                </FaqGrid>
              </CatGroup>
            );
          })
        )}
      </Body>
    </Wrapper>
  );
}

export default FAQSection;
